import {
  ForwardRefExoticComponent,
  RefAttributes,
  forwardRef,
  memo,
  useEffect,
} from 'react';
import { Link, useLocation } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Avatar,
  Chip,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';

// project imports
import config from 'config';
import { useDispatch, useSelector } from 'store';
import { activeItem, openDrawer } from 'store/slices/menu';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

// types
import { LinkTarget, NavItemType } from 'types';
import { IconUserShield } from '@tabler/icons-react';

interface NavItemProps {
  item: NavItemType;
  level: number;
}

// ==============================|| SIDEBAR MENU LIST ITEMS ||============================== //

const { borderRadius } = config;

const NavItem = ({ item, level }: NavItemProps) => {
  const theme = useTheme();
  const location = useLocation();
  const matchesSM = useMediaQuery(theme.breakpoints.down('lg'));

  const dispatch = useDispatch();
  const { drawerOpen, openItem } = useSelector((state) => state.menu);

  const Icon = item?.icon!;
  const itemIcon = item?.icon ? (
    <Icon stroke={1.5} size="1.3rem" />
  ) : (
    <FiberManualRecordIcon
      sx={{
        width: openItem.findIndex((id) => id === item?.id) > -1 ? 8 : 6,
        height: openItem.findIndex((id) => id === item?.id) > -1 ? 8 : 6,
      }}
      fontSize={level > 0 ? 'inherit' : 'medium'}
    />
  );

  let itemTarget: LinkTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  let listItemProps: {
    component:
      | ForwardRefExoticComponent<RefAttributes<HTMLAnchorElement>>
      | string;
    href?: string;
    target?: LinkTarget;
  } = {
    component: forwardRef((props, ref) => (
      <Link ref={ref} {...props} to={item.url!} target={itemTarget} />
    )),
  };
  if (item?.external) {
    listItemProps = { component: 'a', href: `${item.url}`, target: itemTarget };
  }

  const itemHandler = (id: string) => {
    dispatch(activeItem([id]));
    matchesSM && dispatch(openDrawer(false));
  };

  // active menu item on page load
  useEffect(() => {
    const currentIndex = location.pathname.includes(item.url as string);
    if (currentIndex) {
      dispatch(activeItem([item.id!]));
    }
    // eslint-disable-next-line
  }, [location]);

  return (
    <ListItemButton
      {...listItemProps}
      disabled={item.disabled}
      sx={{
        borderRadius: `${borderRadius}px`,
        mb: 0.5,
        alignItems: 'flex-start',
        backgroundColor: level > 1 ? 'transparent !important' : 'inherit',
        p: 0,
        pl: drawerOpen ? `${level * 24}px` : '12px',
        '&.Mui-selected': {
          backgroundColor: theme.palette.brand.moss,
        },
      }}
      selected={openItem?.findIndex((id) => id === item.id) > -1}
      onClick={() => {
        itemHandler(item.id!);
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          borderRadius: `${borderRadius}px`,
          backgroundColor: item.enabledDueToStaffMode
            ? 'rgba(2, 136, 209, 0.1)'
            : undefined,
          width: '100%',
          height: '100%',
          py: level > 1 ? 1 : 1.25,
        }}
      >
        <ListItemIcon sx={{ my: 'auto', minWidth: !item?.icon ? 18 : 36 }}>
          {itemIcon}
        </ListItemIcon>
        {drawerOpen && (
          <ListItemText
            title={String(item.titleString || item.title)}
            primary={
              <Typography
                variant={
                  openItem?.findIndex((id) => id === item.id) > -1
                    ? 'h5'
                    : 'body1'
                }
                color="inherit"
              >
                {item.title}
              </Typography>
            }
            secondary={
              item.caption && (
                <Typography
                  variant="caption"
                  sx={{ ...theme.typography.subMenuCaption }}
                  display="block"
                  gutterBottom
                >
                  {item.caption}
                </Typography>
              )
            }
          />
        )}
        {drawerOpen && item.chip && (
          <Chip
            color={item.chip.color}
            variant={item.chip.variant}
            size={item.chip.size}
            label={item.chip.label}
            avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
          />
        )}
        {drawerOpen && item.enabledDueToStaffMode && (
          <Tooltip title="Enabled due to staff mode">
            <IconUserShield
              stroke={1.5}
              size="1rem"
              style={{ position: 'absolute', right: 2, top: 2, opacity: 0.5 }}
            />
          </Tooltip>
        )}
      </Stack>
    </ListItemButton>
  );
};

export default memo(NavItem);
